import { create } from 'zustand';
import { ProjectRelatedData, WorkTagData, WorkData, WorkDetailData } from '../dto/EntityRepository';

export interface PendingWorkImage {
  file: File;
  width: number;
  height: number;
}

interface WorkViewState {
  activeWork: WorkData | null;
  multiSelectedWork: WorkData[];
  setMultiSelectedWork: (works: WorkData[]) => void;
  /** 기존 multiSelectedWork와 병합. 같은 id가 있으면 해당 항목을 새 값으로 교체, 없으면 끝에 추가 */
  addMultiSelectedWork: (works: WorkData[]) => void;
  cleanMultiSelectedWork: () => void;
  hasChanged: boolean;
  multiHasChanged: boolean;
  imageChanged: boolean;
  pendingImage: PendingWorkImage | null;
  isOverviewOpen: boolean;
  setActiveWork: (activeWork: WorkData | null) => void;
  setOnlyDownloadValue: (allowDownload: boolean) => void;
  setOnlyAccessValue: (isPublic: boolean) => void;
  updateActiveWork: (updates: Partial<WorkData>) => void;
  syncWorkTag: (tag: WorkTagData) => void;
  removeWorkTag: (tagId: number) => void;
  updateImage: (originUrl: string, width: number, height: number) => void;
  updateImageFile: (file: File, width: number, height: number) => void;
  afterDeleteActiveWork: () => void;
  setIsOverviewOpen: (isOpen: boolean) => void;

  activeWorkDetailList: WorkDetailData[];
  setOnlyActiveWorkDetailList: (activeWorkDetailList: WorkDetailData[]) => void;
  setActiveWorkDetailList: (activeWorkDetailList: WorkDetailData[]) => void;
  updateActiveWorkDetailList: (id: string, updates: Partial<WorkDetailData>) => void;
  updateImageActiveWorkDetailList: (id: string, originUrl: string, width: number, height: number) => void;
  afterUpdateWorkDetailBulkInfo: (updatedWorkDetailList: WorkDetailData[]) => void;
  afterDeleteActiveWorkDetail: (id: string) => void;

  activeRelatedProjectList: ProjectRelatedData[];
  setActiveRelatedProjectList: (activeRelatedProjectList: ProjectRelatedData[]) => void;

  clearActiveWork: () => void;
  setMultiHasChanged: (hasChanged: boolean) => void;
}

export const useWorkViewStore = create<WorkViewState>((set) => ({
  activeWork: null,
  multiSelectedWork: [],
  setMultiSelectedWork: (works) =>
    set((state) => {
      if (works.length > 0) {
        return {
          multiSelectedWork: works,
          activeWork: null,
          activeWorkDetailList: [],
          activeRelatedProjectList: [],
          hasChanged: false,
          multiHasChanged: false,
          imageChanged: false,
          pendingImage: null,
          isOverviewOpen: true,
        };
      }
      return {
        multiSelectedWork: [],
        multiHasChanged: false,
        isOverviewOpen: !!state.activeWork,
      };
    }),
  addMultiSelectedWork: (incomingWorks) => {
    set((state) => {
      const nextMultiSelectedWork = [...state.multiSelectedWork];
      for (const work of incomingWorks) {
        const existingIndex = nextMultiSelectedWork.findIndex((item) => item.id === work.id);
        if (existingIndex >= 0) {
          nextMultiSelectedWork[existingIndex] = work;
        } else {
          nextMultiSelectedWork.push(work);
        }
      }
      return {
        multiSelectedWork: nextMultiSelectedWork,
        isOverviewOpen: !!state.activeWork || nextMultiSelectedWork.length > 0,
      };
    });
  },
  cleanMultiSelectedWork: () =>
    set({
      multiSelectedWork: [],
    }),
  hasChanged: false,
  multiHasChanged: false,
  imageChanged: false,
  pendingImage: null,
  isOverviewOpen: false,
  setActiveWork: (activeWork) =>
    set((state) => {
      const nextMulti = activeWork ? [] : state.multiSelectedWork;
      return {
        activeWork,
        multiSelectedWork: nextMulti,
        hasChanged: false,
        multiHasChanged: false,
        imageChanged: false,
        pendingImage: null,
        isOverviewOpen: !!activeWork || nextMulti.length > 0,
      };
    }),
  setOnlyDownloadValue: (allowDownload) =>
    set(({ activeWork, hasChanged }) => ({
      activeWork: activeWork ?
        {
          ...activeWork,
          uploadFile: {
            ...activeWork.uploadFile,
            allowDownload
          }
        }
        : null,
      hasChanged
    })),
  setOnlyAccessValue: (isPublic) =>
    set(({ activeWork, hasChanged }) => ({
      activeWork: activeWork ?
        { ...activeWork, isPublic }
        : null,
      hasChanged
    })),
  setIsOverviewOpen: (isOpen) => set({ isOverviewOpen: isOpen }),
  setMultiHasChanged: (hasChanged) => set({ multiHasChanged: hasChanged }),
  updateActiveWork: (updates) =>
    set(({ activeWork }) => ({
      activeWork: activeWork ?
        { ...activeWork, ...updates }
        : null,
      hasChanged: true
    })),
  syncWorkTag: (tag) =>
    set(({ activeWork, multiSelectedWork }) => ({
      activeWork: activeWork
        ? {
            ...activeWork,
            tags: (activeWork.tags ?? []).map((workTag) =>
              workTag.id === tag.id ? tag : workTag
            ),
          }
        : null,
      multiSelectedWork: multiSelectedWork.map((work) => ({
        ...work,
        tags: (work.tags ?? []).map((workTag) =>
          workTag.id === tag.id ? tag : workTag
        ),
      })),
    })),
  removeWorkTag: (tagId) =>
    set(({ activeWork, multiSelectedWork }) => ({
      activeWork: activeWork
        ? {
            ...activeWork,
            tags: (activeWork.tags ?? []).filter((tag) => tag.id !== tagId),
          }
        : null,
      multiSelectedWork: multiSelectedWork.map((work) => ({
        ...work,
        tags: (work.tags ?? []).filter((tag) => tag.id !== tagId),
      })),
    })),

  updateImage: (originUrl: string, width: number, height: number) =>
    set(({ activeWork }) => ({
      activeWork: activeWork ? {
        ...activeWork,
        uploadFile: {
          ...activeWork.uploadFile,
          originUrl,
          width,
          height,
        }
      } : null,
      pendingImage: null,
      hasChanged: true,
      imageChanged: true,
    })),
  updateImageFile: (file: File, width: number, height: number) =>
    set(({ activeWork }) => activeWork ? ({
      pendingImage: { file, width, height },
      hasChanged: true,
      imageChanged: true,
    }) : ({})),
  afterDeleteActiveWork: () =>
    set({
      activeWork: null,
      multiSelectedWork: [],
      hasChanged: false,
      multiHasChanged: false,
      imageChanged: false,
      pendingImage: null,
      activeWorkDetailList: [],
      activeRelatedProjectList: [],
      isOverviewOpen: false,
    }),

  activeWorkDetailList: [],
  setOnlyActiveWorkDetailList: (activeWorkDetailList) => set({ activeWorkDetailList }),
  setActiveWorkDetailList: (activeWorkDetailList) =>
    set(() => ({
      activeWorkDetailList: activeWorkDetailList.map((awd) => ({ ...awd, hasChanged: false, imageChanged: false })),
    })),
  updateActiveWorkDetailList: (id, updates) =>
    set(({ activeWorkDetailList }) => ({
      activeWorkDetailList: activeWorkDetailList.map((awd) =>
        awd.id === id ?
          { ...awd, ...updates, hasChanged: true }
          : awd
      ),
    })),
  updateImageActiveWorkDetailList: (id, originUrl, width, height) =>
    set(({ activeWorkDetailList }) => ({
      activeWorkDetailList: activeWorkDetailList.map((awd) =>
        awd.id === id ? {
          ...awd,
          uploadFile: {
            ...awd.uploadFile,
            originUrl,
            width,
            height,
          },
          hasChanged: true, imageChanged: true
        } : awd
      ),
    })),
  afterUpdateWorkDetailBulkInfo: (updatedWorkDetailList) =>
    set((state) => {
      if (updatedWorkDetailList.length === 0) return state;
      const updatedById = new Map(updatedWorkDetailList.map((detail) => [detail.id, detail]));
      return {
        activeWorkDetailList: state.activeWorkDetailList.map((detail) => {
          const updated = updatedById.get(detail.id);
          return updated ? { ...updated, hasChanged: false, imageChanged: false } : detail;
        }),
      };
    }),
  afterDeleteActiveWorkDetail: (id) =>
    set(({ activeWorkDetailList }) => ({
      activeWorkDetailList: activeWorkDetailList.filter((awd) => awd.id !== id)
    })),
  activeRelatedProjectList: [],
  setActiveRelatedProjectList: (activeRelatedProjectList) => set({ activeRelatedProjectList }),
  clearActiveWork: () =>
    set({
      activeWork: null,
      multiSelectedWork: [],
      hasChanged: false,
      multiHasChanged: false,
      imageChanged: false,
      pendingImage: null,
      activeWorkDetailList: [],
      activeRelatedProjectList: [],
      isOverviewOpen: false,
    }),
}));
